import { type Item } from '../utils/store';
import NumberInput from './inputs/NumberInput';
import StringInput from './inputs/StringInput';
import {
  endSelection,
  startSelection,
  useInSelection,
} from './itemViewSelection';
import { addUndo } from '@/utils/undoer';
import { Icon } from '@iconify/react';
import clsx from 'clsx';
import { Fragment } from 'react/jsx-runtime';

function SelectableItem({
  row,
  column,
  children,
}: {
  row: number;
  column: number;
  children: React.ReactNode;
}) {
  const isSelected = useInSelection(row, column);

  return (
    <div
      className={clsx(
        'selectable-item h-full w-full',
        isSelected && 'bg-blue-200',
      )}
      onMouseDown={e => {
        if (e.buttons !== 1) return;
        if (e.shiftKey) {
          endSelection(row, column);
          return;
        }
        startSelection(row, column);
      }}
      onMouseEnter={e => {
        if (e.buttons === 1) {
          endSelection(row, column);
        }
      }}
    >
      {children}
    </div>
  );
}

export default function ItemView({
  row,
  item,
  onChange,
  onDelete,
  onInsertAbove,
  onEnter,
}: {
  row: number;
  item: Item;
  onChange: (item: Item) => void;
  onDelete: () => void;
  onInsertAbove: () => void;
  onEnter: (input: 'stockNumber' | 'description' | number) => void;
}) {
  const { stockNumber, description, serials, quantities } = item;

  const showSerials = !!(description || (serials && serials.length));

  return (
    <tr className="border-b">
      <td className="border-l text-left text-gray-800">
        <SelectableItem row={row} column={0}>
          <StringInput
            id={`stockNumber-${row}`}
            value={stockNumber}
            onChange={value => onChange({ ...item, stockNumber: value })}
            onEnter={() => onEnter('stockNumber')}
          />
        </SelectableItem>
      </td>
      <td className="border-l text-left text-gray-800">
        <SelectableItem row={row} column={1}>
          <div className="flex flex-wrap">
            <StringInput
              id={`description-${row}`}
              className={clsx(!showSerials && 'w-full')}
              value={description}
              onChange={value => onChange({ ...item, description: value })}
              onEnter={() => onEnter('description')}
            />
            {showSerials && (
              <SerialsInput
                serials={serials}
                onChange={value => onChange({ ...item, serials: value })}
                onEnter={() => onEnter('description')}
              />
            )}
          </div>
        </SelectableItem>
      </td>
      {quantities.map((quantity, index) => (
        <td key={index} className="w-0 border-l text-left text-gray-800">
          <SelectableItem row={row} column={2 + index}>
            <NumberInput
              value={quantity}
              onChange={value => {
                const newQuantities = [...quantities];
                newQuantities[index] = value ?? null;
                onChange({ ...item, quantities: newQuantities });
              }}
              onEnter={() => onEnter(index)}
            />
          </SelectableItem>
        </td>
      ))}
      <td className="w-0 border-l text-left text-gray-800 print:hidden">
        <div className="flex">
          <button
            className="mr-4 cursor-pointer rounded bg-red-500 p-1 font-bold text-white hover:bg-red-700"
            onClick={onDelete}
          >
            <Icon icon="mdi:delete" className="h-6 w-6" />
          </button>
          <button
            className="cursor-pointer rounded bg-blue-500 p-1 font-bold text-white hover:bg-blue-700"
            onClick={onInsertAbove}
          >
            <Icon icon="mdi:table-row-plus-before" className="h-6 w-6" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function SerialsInput({
  serials,
  onChange,
  onEnter,
}: {
  serials: string[] | undefined;
  onChange: (serials: string[] | undefined) => void;
  onEnter: () => void;
}) {
  return (
    <div className="ml-2 flex flex-wrap">
      {[...(serials ?? []), ''].map((serial, index) => (
        <Fragment key={index}>
          <div
            className={clsx(
              'my-auto h-5 border-l-2 border-dashed border-gray-400',
              (serials?.length ?? 0) === 0 && 'print:hidden',
            )}
          />
          <StringInput
            className={clsx(
              'inline text-blue-700 italic',
              (serials?.length ?? 0) === index && 'print:hidden',
            )}
            placeholder={(serials?.length ?? 0) === 0 ? 'Serials' : '...'}
            value={serial}
            onChange={value => {
              const newSerials = [...(serials ?? [])];
              newSerials[index] = value;
              onChange(newSerials);
            }}
            onBlur={() => {
              if (index === serials?.length || serial.trim().length > 0) return;
              const ogSerials = serials ?? [];
              const newSerials = [...(serials ?? [])];
              newSerials.splice(index, 1);
              onChange(newSerials);

              addUndo(
                () => onChange(ogSerials),
                () => onChange(newSerials),
              );
            }}
            onEnter={onEnter}
            allowNewline={false}
          />
        </Fragment>
      ))}
    </div>
  );
}
